/**
 * Platform-agnostic WebSocket client for Nordex backend with channel subscriptions.
 * 
 * Works in both web browsers and React Native.
 * 
 * Supports both public and authenticated channels:
 * - Public: orderbook/{symbol}, price/{symbol}, funding/live/{symbol}, liquidations/{symbol}
 * - User (requires JWT): user/positions, user/orders, user/balances, user/tx-status
 */

import { getWsBaseUrl, isDebugEnabled } from "../config";

/** WebSocket message types from the backend */
export type WsMessageType =
  | "orderbook"
  | "price"
  | "prices"
  | "stats"
  | "vlp_quote"
  | "funding_rate"
  | "liquidation"
  | "position"
  | "order"
  | "balance"
  | "fill"
  | "funding_payment"
  | "tx_status"
  | "candles"
  | "candle_update"
  | "error"
  | "subscribed"
  | "unsubscribed"
  | "pong";

/** Base WebSocket message structure */
export interface WsMessage {
  type: WsMessageType;
  [key: string]: unknown;
}

/** Orderbook message */
export interface WsOrderbookMessage extends WsMessage {
  type: "orderbook";
  symbol: string;
  bids: Array<{ price: string; size: string }>;
  asks: Array<{ price: string; size: string }>;
  timestamp: number;
}

/** Price message */
export interface WsPriceMessage extends WsMessage {
  type: "price";
  symbol: string;
  price: string;
  sources: Array<{ source: string; price: string }>;
  timestamp: number;
  price_change_24h?: string;
  price_change_percent_24h?: string;
}

/** Price data for a single market (used in Prices message) */
export interface WsPriceData {
  market: string;
  price: string;
  sources: Array<{ source: string; price: string }>;
  updated_at: string;
}

/** All prices message (batch update for all markets) */
export interface WsPricesMessage extends WsMessage {
  type: "prices";
  prices: WsPriceData[];
  timestamp: number;
}

/** Market stat data for a single market (used in Stats message) */
export interface WsMarketStatData {
  market: string;
  market_price: string;          // Binance Perpetuals - used for trading
  oracle_price: string;          // Pyth oracle - used for funding & liquidations
  open_interest_usd: string;
  volume_24h_usd: string;
  funding_rate: string;
  next_funding_at: string;
  max_leverage: number;
  price_change_24h?: string;        // 24H absolute price change
  price_change_percent_24h?: string; // 24H percentage change
  price_24h_ago?: string;           // Reference price from 24h ago
}

/** All market stats message (batch update for all markets) */
export interface WsStatsMessage extends WsMessage {
  type: "stats";
  stats: WsMarketStatData[];
  timestamp: number;
}

/** Position message */
export interface WsPositionMessage extends WsMessage {
  type: "position";
  id: string;
  user_id: string;
  market: string;
  side: string;
  size: string;
  entry_price: string;
  mark_price: string;
  leverage: string;
  margin: string;
  unrealized_pnl: string;
  realized_pnl: string;
  liquidation_price: string | null;
  opened_at: string;
  take_profit: string | null;
  stop_loss: string | null;
  timestamp: number;
}

/** Order message */
export interface WsOrderMessage extends WsMessage {
  type: "order";
  order_id: string;
  user_id: string;
  market: string;
  side: string;
  order_type: string;
  price: string;
  size: string;
  filled_size: string;
  status: string;
  timestamp: number;
}

/** Balance message */
export interface WsBalanceMessage extends WsMessage {
  type: "balance";
  user_id: string;
  collateral: string;
  available_margin: string;
  used_margin: string;
  unrealized_pnl: string;
  timestamp: number;
}

/** Fill/trade message (for real-time trade history) */
export interface WsFillMessage extends WsMessage {
  type: "fill";
  id: string;
  user_id: string;
  order_id: string;
  market: string;
  side: string;
  size: string;
  price: string;
  fee: string;
  realized_pnl: string;
  fill_type: string;
  timestamp: number;
}

/** Funding payment message (for real-time funding history) */
export interface WsFundingMessage extends WsMessage {
  type: "funding_payment";
  id: string;
  user_id: string;
  market: string;
  position_size: string;
  funding_rate: string;
  payment: string;
  timestamp: number;
}

/** VLP Quote message */
export interface WsVlpQuoteMessage extends WsMessage {
  type: "vlp_quote";
  market: string;
  bid_price: string;
  ask_price: string;
  spread: string;
  oracle_price: string;
  vlp_position: string;
  timestamp: number;
}

/** Candles message (initial load or full refresh) */
export interface WsCandlesMessage extends WsMessage {
  type: "candles";
  market: string;
  interval: string;
  candles: Array<{
    time: number;
    open: string;
    high: string;
    low: string;
    close: string;
    volume: string;
  }>;
  timestamp: number;
}

/** Candle update message (real-time single candle update) */
export interface WsCandleUpdateMessage extends WsMessage {
  type: "candle_update";
  market: string;
  interval: string;
  candle: {
    time: number;
    open: string;
    high: string;
    low: string;
    close: string;
    volume: string;
  };
  is_closed: boolean;
  timestamp: number;
}

/** Connection state */
export type ConnectionState = "disconnected" | "connecting" | "connected" | "reconnecting";

/** Listener callback type */
export type WsListener<T extends WsMessage = WsMessage> = (message: T) => void;

/** Reconnection configuration */
interface ReconnectConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
}

const DEFAULT_RECONNECT_CONFIG: ReconnectConfig = {
  maxAttempts: 10,
  baseDelay: 1000,
  maxDelay: 30000,
};

/**
 * Log helper that respects debug mode
 */
function log(message: string, ...args: unknown[]): void {
  if (isDebugEnabled()) {
    console.log(`[WS] ${message}`, ...args);
  }
}

/**
 * WebSocket channel connection manager.
 * Creates a single WebSocket connection per channel endpoint.
 */
/** Callback for connection state changes */
export type StateChangeListener = (state: ConnectionState) => void;

class WsChannel {
  ws: WebSocket | null = null;
  listeners: Set<WsListener> = new Set();
  stateListeners: Set<StateChangeListener> = new Set();
  state: ConnectionState = "disconnected";
  reconnectAttempts = 0;
  reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  pingInterval: ReturnType<typeof setInterval> | null = null;
  lastMessageTime = 0;
  url: string;
  reconnectConfig: ReconnectConfig;
  // Flag to prevent auto-reconnect during intentional close/update
  intentionalClose = false;
  // Counter to track connection attempts and detect stale callbacks
  connectionId = 0;
  // Grace period timeout for disconnect (prevents reconnect cycles from React effect cleanup)
  disconnectGraceTimeout: ReturnType<typeof setTimeout> | null = null;
  // Auth gating properties
  requiresAuth = false;  // Whether this channel needs authentication
  isWaitingForAuth = false;  // Whether we're paused waiting for token
  getToken: () => string | null;  // Function to get current token from parent
  
  // Browser event handler references for cleanup (visibility/connectivity handling)
  private visibilityHandler: (() => void) | null = null;
  private onlineHandler: (() => void) | null = null;
  private offlineHandler: (() => void) | null = null;
  // Timeout for verifying ping response after visibility restore
  private visibilityPingTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(url: string, requiresAuth: boolean, getToken: () => string | null, reconnectConfig: ReconnectConfig = DEFAULT_RECONNECT_CONFIG) {
    this.url = url;
    this.requiresAuth = requiresAuth;
    this.getToken = getToken;
    this.reconnectConfig = reconnectConfig;
    
    // Setup browser event listeners for visibility/connectivity handling
    this.setupBrowserEventListeners();
  }

  /** Check if we have required authentication */
  hasRequiredAuth(): boolean {
    return !this.requiresAuth || !!this.getToken();
  }

  /** Get current connection state */
  getState(): ConnectionState {
    return this.state;
  }

  /** Subscribe to connection state changes (event-driven, no polling) */
  subscribeToStateChange(callback: StateChangeListener): () => void {
    this.stateListeners.add(callback);
    return () => {
      this.stateListeners.delete(callback);
    };
  }

  private notifyStateListeners(): void {
    this.stateListeners.forEach((cb) => {
      try {
        cb(this.state);
      } catch (err) {
        console.error("[WS] State listener error:", err);
      }
    });
  }

  /** Connect to the WebSocket endpoint (only if auth requirements met) */
  connect(): void {
    // Check authentication requirements BEFORE attempting connection
    if (!this.hasRequiredAuth()) {
      log(`Skipping connection to ${this.url} - waiting for authentication`);
      this.isWaitingForAuth = true;
      this.state = "disconnected";
      this.notifyStateListeners();
      return;
    }
    
    this.isWaitingForAuth = false;
    
    // Prevent duplicate connections
    if (this.ws?.readyState === WebSocket.OPEN || 
        this.ws?.readyState === WebSocket.CONNECTING ||
        this.state === "connecting") {
      return;
    }

    // Increment connection ID to invalidate any pending callbacks from previous connections
    this.connectionId++;
    const currentConnectionId = this.connectionId;

    this.state = this.reconnectAttempts > 0 ? "reconnecting" : "connecting";
    this.notifyStateListeners();
    log(`Connecting to ${this.url} (id: ${currentConnectionId})`);
    
    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        // Ignore if this is a stale callback from a previous connection attempt
        if (currentConnectionId !== this.connectionId) {
          log(`Ignoring stale onopen (id: ${currentConnectionId}, current: ${this.connectionId})`);
          return;
        }
        log(`Connected to ${this.url}`);
        this.state = "connected";
        this.notifyStateListeners();
        this.reconnectAttempts = 0;
        // Reset lastMessageTime so stale check doesn't trigger immediately
        this.lastMessageTime = Date.now();
        this.startPingInterval();
      };

      this.ws.onmessage = (event) => {
        // Ignore if stale
        if (currentConnectionId !== this.connectionId) return;
        
        this.lastMessageTime = Date.now();
        try {
          const message: WsMessage = JSON.parse(event.data);
          this.notifyListeners(message);
        } catch (error) {
          console.error("[WS] Failed to parse message:", error);
        }
      };

      this.ws.onerror = (error) => {
        // Ignore if stale
        if (currentConnectionId !== this.connectionId) return;
        console.error("[WS] Error:", error);
      };

      this.ws.onclose = (event) => {
        // Ignore if this is a stale callback
        if (currentConnectionId !== this.connectionId) {
          log(`Ignoring stale onclose (id: ${currentConnectionId}, current: ${this.connectionId})`);
          return;
        }
        
        log(`Disconnected from ${this.url}`, event.code, event.reason);
        this.ws = null;
        this.state = "disconnected";
        this.notifyStateListeners();
        this.stopPingInterval();
        
        // Only reconnect if we have listeners AND this wasn't an intentional close
        if (this.listeners.size > 0 && !this.intentionalClose) {
          this.scheduleReconnect();
        }
        // Reset the flag after handling
        this.intentionalClose = false;
      };
    } catch (error) {
      console.error("[WS] Failed to connect:", error);
      this.state = "disconnected";
      this.notifyStateListeners();
      this.scheduleReconnect();
    }
  }

  /** Disconnect from the WebSocket endpoint */
  disconnect(): void {
    // Increment connectionId to invalidate any pending callbacks
    this.connectionId++;
    
    // Clear any pending grace timeout
    if (this.disconnectGraceTimeout) {
      clearTimeout(this.disconnectGraceTimeout);
      this.disconnectGraceTimeout = null;
    }
    
    // Cleanup browser event listeners to prevent memory leaks
    this.cleanupBrowserEventListeners();
    
    this.stopReconnect();
    this.stopPingInterval();
    
    if (this.ws) {
      // Set flag to prevent auto-reconnect in onclose handler
      this.intentionalClose = true;
      this.ws.close(1000, "Client disconnect");
      this.ws = null;
    }
    
    this.state = "disconnected";
    this.notifyStateListeners();
    this.reconnectAttempts = 0;
  }

  /** Update URL and reconnect (used when token changes) */
  updateUrlAndReconnect(newUrl: string): void {
    // Increment connectionId to invalidate any pending callbacks from old connection
    this.connectionId++;
    
    this.url = newUrl;
    this.stopReconnect();
    this.stopPingInterval();
    
    if (this.ws) {
      // Set flag to prevent auto-reconnect in onclose handler
      // (we'll manually reconnect with the new URL below)
      this.intentionalClose = true;
      this.ws.close(1000, "Token changed");
      this.ws = null;
    }
    
    this.state = "disconnected";
    this.notifyStateListeners();
    this.reconnectAttempts = 0;
    
    // Reconnect with new URL if we have listeners AND auth requirements are met
    if (this.listeners.size > 0) {
      if (this.hasRequiredAuth()) {
        this.connect();
      } else {
        log(`Waiting for authentication before reconnecting to ${this.url}`);
        this.isWaitingForAuth = true;
      }
    }
  }

  /** Subscribe to messages */
  subscribe(listener: WsListener): () => void {
    // Cancel any pending disconnect grace timeout
    if (this.disconnectGraceTimeout) {
      clearTimeout(this.disconnectGraceTimeout);
      this.disconnectGraceTimeout = null;
    }
    
    this.listeners.add(listener);
    
    // Auto-connect when first listener subscribes (or we have listeners but disconnected)
    if (this.listeners.size >= 1 && this.state === "disconnected") {
      this.connect();
    }
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
      
      // Use grace period before disconnecting to handle React effect cleanup/setup cycles
      // This prevents disconnect→reconnect when effects re-run
      if (this.listeners.size === 0) {
        this.disconnectGraceTimeout = setTimeout(() => {
          this.disconnectGraceTimeout = null;
          // Double-check no new listeners were added during grace period
          if (this.listeners.size === 0) {
            this.disconnect();
          }
        }, 100); // 100ms grace period
      }
    };
  }

  /** Get number of active listeners */
  getListenerCount(): number {
    return this.listeners.size;
  }

  notifyListeners(message: WsMessage): void {
    this.listeners.forEach((listener) => {
      try {
        listener(message);
      } catch (error) {
        console.error("[WS] Listener error:", error);
      }
    });
  }

  scheduleReconnect(): void {
    // Don't schedule if already pending
    if (this.reconnectTimeout) {
      return;
    }
    
    // Don't retry if waiting for auth
    if (this.isWaitingForAuth || !this.hasRequiredAuth()) {
      log("Skipping reconnect - waiting for authentication");
      this.isWaitingForAuth = true;
      return;
    }
    
    if (this.reconnectAttempts >= this.reconnectConfig.maxAttempts) {
      log("Max reconnect attempts reached");
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectConfig.baseDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.reconnectConfig.maxDelay
    );

    log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.connect();
    }, delay);
  }

  stopReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  startPingInterval(): void {
    // Send ping every 25 seconds to keep connection alive
    // Most servers/proxies have 60s idle timeout, so 25s is safe
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        // Send a ping to keep the connection alive
        try {
          // Send WebSocket ping frame
          this.ws.send(JSON.stringify({ action: "ping" }));
        } catch (error) {
          console.error("[WS] Failed to send ping:", error);
        }
        
        // If no message received in 90 seconds (3 missed pings), reconnect
        if (Date.now() - this.lastMessageTime > 90000) {
          log("Connection stale (no response), reconnecting...");
          this.connectionId++; // Invalidate callbacks
          this.intentionalClose = true;
          this.stopReconnect();
          this.stopPingInterval();
          if (this.ws) {
            this.ws.close(1000, "Stale connection");
            this.ws = null;
          }
          this.state = "disconnected";
          this.notifyStateListeners();
          this.reconnectAttempts = 0;
          this.connect();
        }
      }
    }, 25000);
  }

  stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Setup browser event listeners for visibility and connectivity changes.
   * Industry standard for maintaining WebSocket health when users return to tab
   * or when network connectivity is restored.
   */
  setupBrowserEventListeners(): void {
    // Only run in browser environment (not Node.js/React Native without document)
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    // 1. VISIBILITY CHANGE - When user returns to tab
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.handleVisibilityRestore();
      }
      // Note: We don't pause pings when hidden because tab title shows price updates
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    // 2. ONLINE EVENT - When network is restored
    this.onlineHandler = () => {
      log('Network online - verifying connection');
      this.handleConnectivityRestore();
    };
    window.addEventListener('online', this.onlineHandler);

    // 3. OFFLINE EVENT - When network is lost
    this.offlineHandler = () => {
      log('Network offline - connection will reconnect when online');
      // Mark state but don't try to reconnect (no network available)
      if (this.state === 'connected') {
        this.state = 'disconnected';
        this.notifyStateListeners();
      }
    };
    window.addEventListener('offline', this.offlineHandler);
  }

  /**
   * Handle visibility restore - immediately verify connection health.
   * Called when user returns to the tab after being away.
   */
  private handleVisibilityRestore(): void {
    // Only process if we have active listeners (someone cares about this channel)
    if (this.listeners.size === 0) {
      return;
    }

    log('Tab became visible - checking connection health');
    
    // If we're disconnected but have listeners, try to connect
    if (this.state === 'disconnected') {
      log('Reconnecting after visibility restore');
      this.reconnectAttempts = 0; // Reset attempts for fresh start
      this.connect();
      return;
    }

    // If we think we're connected, verify it's actually alive
    if (this.ws?.readyState === WebSocket.OPEN) {
      const timeSinceLastMessage = Date.now() - this.lastMessageTime;
      
      // If it's been more than 30 seconds since last message, connection may be stale
      if (timeSinceLastMessage > 30000) {
        log(`Connection may be stale (${Math.round(timeSinceLastMessage / 1000)}s since last message) - sending immediate ping`);
        this.sendImmediatePing();
        
        // Clear any existing timeout
        if (this.visibilityPingTimeout) {
          clearTimeout(this.visibilityPingTimeout);
        }
        
        // If no response within 5 seconds, force reconnect
        const lastMessageBeforePing = this.lastMessageTime;
        this.visibilityPingTimeout = setTimeout(() => {
          this.visibilityPingTimeout = null;
          // Check if we received any message since the ping
          if (this.lastMessageTime === lastMessageBeforePing) {
            log('No ping response after visibility restore - reconnecting');
            this.forceReconnect();
          }
        }, 5000);
      } else {
        // Connection is fresh, just send a quick ping to be safe
        this.sendImmediatePing();
      }
    } else if (this.ws?.readyState === WebSocket.CONNECTING) {
      // Already connecting, do nothing
      log('Already connecting, waiting...');
    } else {
      // Connection is not open (CLOSING or CLOSED state), reconnect
      log('Connection not open after visibility restore - reconnecting');
      this.reconnectAttempts = 0;
      this.connect();
    }
  }

  /**
   * Handle network connectivity restore.
   * Called when the browser regains network access.
   */
  private handleConnectivityRestore(): void {
    // Only process if we have active listeners
    if (this.listeners.size === 0) {
      return;
    }

    // Reset reconnect attempts since this is a fresh network state
    this.reconnectAttempts = 0;

    if (this.state === 'disconnected' || this.ws?.readyState !== WebSocket.OPEN) {
      log('Network restored - reconnecting');
      this.connect();
    } else {
      // Verify connection is still alive with immediate ping
      this.sendImmediatePing();
    }
  }

  /**
   * Send an immediate ping to verify connection is alive.
   * Used for connection health checks after visibility/connectivity changes.
   */
  private sendImmediatePing(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ action: 'ping' }));
        log('Sent immediate ping');
      } catch (error) {
        log('Failed to send immediate ping - reconnecting');
        this.forceReconnect();
      }
    }
  }

  /**
   * Force a clean reconnection.
   * Used when connection is detected as stale after visibility restore.
   */
  private forceReconnect(): void {
    // Invalidate any pending callbacks from old connection
    this.connectionId++;
    this.intentionalClose = true;
    this.stopReconnect();
    this.stopPingInterval();
    
    // Clear visibility ping timeout if pending
    if (this.visibilityPingTimeout) {
      clearTimeout(this.visibilityPingTimeout);
      this.visibilityPingTimeout = null;
    }
    
    if (this.ws) {
      this.ws.close(1000, 'Force reconnect');
      this.ws = null;
    }
    
    this.state = 'disconnected';
    this.notifyStateListeners();
    this.reconnectAttempts = 0;
    this.connect();
  }

  /**
   * Cleanup browser event listeners.
   * Called during disconnect to prevent memory leaks.
   */
  private cleanupBrowserEventListeners(): void {
    if (typeof document !== 'undefined' && this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (typeof window !== 'undefined') {
      if (this.onlineHandler) {
        window.removeEventListener('online', this.onlineHandler);
        this.onlineHandler = null;
      }
      if (this.offlineHandler) {
        window.removeEventListener('offline', this.offlineHandler);
        this.offlineHandler = null;
      }
    }
    // Clear any pending visibility ping timeout
    if (this.visibilityPingTimeout) {
      clearTimeout(this.visibilityPingTimeout);
      this.visibilityPingTimeout = null;
    }
  }
}

/**
 * WebSocket client manager.
 * Manages multiple channel connections and provides a unified interface.
 */
class WsClient {
  channels: Map<string, WsChannel> = new Map();
  token: string | null = null;

  /** Set the JWT token for authenticated channels */
  setToken(token: string | null): void {
    const oldToken = this.token;
    this.token = token;
    
    // If token changed, update all affected channels
    if (token !== oldToken) {
      for (const [channel, wsChannel] of this.channels) {
        if (wsChannel.requiresAuth) {
          if (token) {
            // Token now available - connect if we have listeners
            if (wsChannel.isWaitingForAuth && wsChannel.getListenerCount() > 0) {
              log(`Token available - reconnecting ${channel}`);
              wsChannel.isWaitingForAuth = false;
              const newUrl = this.buildUrl(channel);
              wsChannel.updateUrlAndReconnect(newUrl);
            } else if (wsChannel.getListenerCount() > 0) {
              // Already connected but token changed - reconnect
              const newUrl = this.buildUrl(channel);
              wsChannel.updateUrlAndReconnect(newUrl);
            }
          } else {
            // Token cleared - disconnect and wait (don't retry)
            log(`Token cleared - pausing ${channel}`);
            wsChannel.intentionalClose = true;
            wsChannel.disconnect();
            wsChannel.isWaitingForAuth = true;
          }
        }
      }
    }
  }

  /** Get JWT token */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Subscribe to a channel.
   * @param channel Channel name (e.g., "orderbook/BTC-USD", "user/positions")
   * @param listener Callback for messages
   * @returns Unsubscribe function
   */
  subscribe<T extends WsMessage = WsMessage>(
    channel: string,
    listener: WsListener<T>
  ): () => void {
    const wsChannel = this.getOrCreateChannel(channel);
    return wsChannel.subscribe(listener as WsListener);
  }

  /**
   * Get connection state for a channel.
   */
  getState(channel: string): ConnectionState {
    const wsChannel = this.channels.get(channel);
    return wsChannel?.getState() ?? "disconnected";
  }

  /**
   * Subscribe to connection state changes for a channel (event-driven, no polling).
   */
  subscribeToStateChange(channel: string, callback: StateChangeListener): () => void {
    const wsChannel = this.getOrCreateChannel(channel);
    return wsChannel.subscribeToStateChange(callback);
  }

  /**
   * Disconnect all channels.
   */
  disconnectAll(): void {
    for (const channel of this.channels.values()) {
      channel.disconnect();
    }
    this.channels.clear();
  }

  getOrCreateChannel(channel: string): WsChannel {
    let wsChannel = this.channels.get(channel);
    
    if (!wsChannel) {
      const url = this.buildUrl(channel);
      const requiresAuth = channel.startsWith("user/") || channel === "events";
      wsChannel = new WsChannel(url, requiresAuth, () => this.getToken());
      this.channels.set(channel, wsChannel);
    }
    
    return wsChannel;
  }

  buildUrl(channel: string): string {
    const wsBaseUrl = getWsBaseUrl();
    
    // Map channel names to WebSocket endpoints
    const parts = channel.split("/");
    let url: string;
    
    switch (parts[0]) {
      case "orderbook":
        url = `${wsBaseUrl}/ws/orderbook/${parts[1]}`;
        break;
      case "price":
        url = `${wsBaseUrl}/ws/price/${parts[1]}`;
        break;
      case "prices":
        // All oracle prices in a single stream
        url = `${wsBaseUrl}/ws/prices`;
        break;
      case "stats":
        // All market stats in a single stream
        url = `${wsBaseUrl}/ws/stats`;
        break;
      case "vlp":
        url = `${wsBaseUrl}/ws/vlp/${parts[1]}`;
        break;
      case "funding":
        url = `${wsBaseUrl}/ws/funding/live/${parts[2]}`;
        break;
      case "liquidations":
        url = `${wsBaseUrl}/ws/liquidations/${parts[1]}`;
        break;
      case "candles":
        // Candle data channel: candles/{market}/{interval}
        url = `${wsBaseUrl}/ws/candles/${parts[1]}/${parts[2]}`;
        break;
      case "user":
        // User channels require token
        const endpoint = parts[1]; // positions, orders, balances, tx-status
        const tokenParam = this.token ? `?token=${encodeURIComponent(this.token)}` : "";
        url = `${wsBaseUrl}/ws/user/${endpoint}${tokenParam}`;
        break;
      case "events":
        // User-specific events channel (requires authentication)
        // Provides real-time notifications for user's own trading activity
        // Server filters events by user_id for security
        const eventsTokenParam = this.token ? `?token=${encodeURIComponent(this.token)}` : "";
        url = `${wsBaseUrl}/ws/user/events${eventsTokenParam}`;
        break;
      case "duel":
        // Public duel lobby events (e.g. duel/lobby)
        url = `${wsBaseUrl}/ws/duel/${parts[1]}`;
        break;
      default:
        throw new Error(`Unknown channel: ${channel}`);
    }
    
    return url;
  }
}

// Export singleton instance
export const wsClient = new WsClient();

// Export types for convenience
export type { WsChannel, WsClient };

